import {AsyncNotice} from "./notices/notice";
import {getCookie, setCookie} from "./utilities/cookies";
import FetchHelpers from "./fetch-helpers";
import {getUserFontSizeFromUserSettings} from "./utilities/general";

/**
 * Get the current font-size for table of contents and reader. Check for a value, in this order:
 * - if signed in:
 *   - from session Storage
 *   - from cookie
 *   - specified element in the DOM.
 *   - default font size value of '1' (rem)
 *
 * @returns {string}
 */
function getCurrentFontSizeOnClient() {
  // from cookie
  const cookieFontSize = getCookie('fontSize');
  if (cookieFontSize) {
    return cookieFontSize;
  }

  // from local Storage
  const localFontSize = localStorage.getItem('fontSize');
  if (localFontSize) {
    return localFontSize;
  }

  // from User Settings
  const dbFontSize = getUserFontSizeFromUserSettings();
  if (dbFontSize) {
    return dbFontSize;
  }

  // from CSS
  const cssFontSize = fontSizeInMinistryTextElem();
  if (cssFontSize) {
    return cssFontSize.toString();
  }

  return '';
}

/**
 * Async fetch of the font-size stored in the User's settings
 *
 * @returns {Promise<string>}
 */
async function getFontSizeFromDB() {
  return await fontSizeFetch();
}

/**
 * Async set of the font-size to the User's settings
 *
 * @param fontSize
 * @returns {Promise<string>}
 */
async function saveFontSizeToDB(fontSize) {
  return await fontSizeFetch(fontSize);
}

/**
 * Fetch call to backend to either fetch or save the font size.
 *
 * @param fontSize
 * @returns {Promise<string>}
 */
async function fontSizeFetch(fontSize = null) {
  let info = document.querySelector('.current-info');
  let result = {'success': false};

  if(info) {
    const {userId, fetchUrl} = info.dataset;
    const url = `${fetchUrl}font-size.php`;
    let action = 'get';

    if (fontSize) {
      action = 'post';
    }

    let data = {userId, action, fontSize};
    result = await new FetchHelpers().fetchData('POST', url, data);
  }

  if (result.success) {
    return result.fontSize;
  } else {
    let msg = 'Font size could not be retrieved';
    let notice = new AsyncNotice(msg);

    await notice.create({}, true);
    notice.remove(5);
  }

  return '';
}

/**
 * Grab the fontSize on the ministry text paragraph element.
 *
 * @returns {number}
 */
function fontSizeInMinistryTextElem() {
  const ministryTextParagraph = document.querySelector('#ministry-text p');
  if (ministryTextParagraph) {
    let sizeFromCss = window.getComputedStyle(ministryTextParagraph).fontSize.toString();
    let size = (sizeFromCss.substring(0, sizeFromCss.length - 2) / 12).toFixed(2);

    return parseFloat(size);
  }
}

/**
 * Initialize the font size display element.
 */
function initialize(au) {
  let fontSize = '1'; // init to 1rem;
  const clientFontSize = getCurrentFontSizeOnClient();

  const ministryTextElem = document.querySelector('#ministry-text');
  if(ministryTextElem) {
    let { dataset: { appSession = 'false' } } = ministryTextElem;
    appSession = (appSession === 'true');
    //if appSession, use font size from userSettings (DB)
    if(appSession && au) {
      fontSize = getUserFontSizeFromUserSettings();
    } else {
      if (clientFontSize.length > 0) {
        // cookie should not be set to 'null'
        if(clientFontSize === 'null') {
          setCookie("fontSize", fontSize, 86400 * 365, '/');
        }

        fontSize = clientFontSize;
      }
    }
  }

  // set the font-size on the client
  storeFontSizeToStorages(fontSize);

  const { style: ministryTextStyle } = document.querySelector("#ministry-text");
  const currentFontSizeElem = document.querySelector('#current-font-size')

  if (currentFontSizeElem && ministryTextStyle) {
    // set font size on font element
    currentFontSizeElem.textContent = `${((fontSize * 12).toFixed(1))}pt`;
    ministryTextStyle.setProperty('font-size', `${fontSize}rem`);
  }
}

/**
 * Method to resize the text on a book's Table of contents or a reader
 * @param multiplier
 */
async function resizeText(multiplier, au) {
  const ministryTextElem = document.querySelector("#ministry-text");
  const currentFontSizeElem = document.querySelector('#current-font-size')
  let { dataset: { appSession = 'false' } } = ministryTextElem;
  appSession = (appSession === 'true'); // cast to boolean
  let displayError = false;

  if(currentFontSizeElem) {
    let currentSize = getCurrentFontSizeOnClient();
    let newSizePt, newSizeValue, newSizeRem;

    if (multiplier === 0) {
      newSizeValue = 1;
    } else {
      currentSize = currentSize ? currentSize : 1; // set default if necessary
      newSizeValue = parseFloat((parseFloat(currentSize) + (multiplier * 0.1)).toFixed(1));
    }

    if (typeof (newSizeValue) === 'number') {
      newSizeRem = `${newSizeValue.toString()}rem`;
      newSizePt = `${(newSizeValue * 12).toFixed(1)}pt`;

      // store the new value
      if(appSession) {
        storeFontSizeToStorages(newSizeValue, au); // if app session, save to DB if authenticated
      } else {
        storeFontSizeToStorages(newSizeValue);
      }

      // change the font size on reader
      ministryTextElem.style.setProperty('font-size', newSizeRem);

      // change displayed font size
      currentFontSizeElem.textContent = newSizePt;

      console.log(`new font-size: ${newSizePt}`);

      return newSizeValue;
    } else {
      displayError = true;
    }
  } else {
    displayError = true;
  }

  if(displayError) {
    const notice = new AsyncNotice('Could not set font size setting.');
    await notice.create({}, true);

    notice.remove();
  }
}

/**
 * Store font size to session and cookie storage
 * @param fontSize
 * @param saveToDb
 */
function storeFontSizeToStorages(fontSize, saveToDb = false) {
  setCookie("fontSize", fontSize, 86400 * 365, '/');
  // sessionStorage.setItem("fontSize", fontSize);
  // localStorage.setItem('fontSize', fontSize);
  if(saveToDb) {
    saveFontSizeToDB(fontSize).then(() => { console.log('Font-size saved to DB.'); });
  }
}

export { getCurrentFontSizeOnClient, initialize, resizeText, storeFontSizeToStorages, saveFontSizeToDB }
